import { StateStore } from './StateStore'
import { EventRegistry } from './EventRegistry'
import { Logger } from './Logger'
import { BlockEngine } from '../blocks-core/BlockEngine'
import { PythonExecutor } from '../../core/PythonExecutor/PythonExecutor'
import { createBlockProxy } from './proxies/BlockProxy'
import message from 'antd/lib/message'
import Modal from 'antd/lib/modal'

export type ModuleExtension = Record<string, unknown>

export type BaseModule = {
    state: {
        get: (key: string, defaultValue?: unknown) => unknown
        set: (key: string, value: unknown) => void
    }
    ui: {
        showMessage: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void
        showModal: (title: string, content: string) => void
    }
    events: {
        register: (
            selectorType: string,
            selectorValue: string,
            eventType: string,
            handlerId: string
        ) => string
        unregister: (
            selectorType: string,
            selectorValue: string,
            eventType: string,
            handlerId?: string
        ) => void
    }
    log: {
        error: (
            msg: string,
            error: string,
            context: Record<string, unknown>,
            source: string
        ) => void
        warn: (msg: string, context: Record<string, unknown>, source: string) => void
        info: (msg: string, context: Record<string, unknown>, source: string) => void
        debug: (msg: string, context: Record<string, unknown>, source: string) => void
    }
    block: {
        getById: (blockId: string) => Record<string, (...args: unknown[]) => unknown> | undefined
        getByName: (name: string) => Record<string, (...args: unknown[]) => unknown>[] | undefined
    }
    editable: {
        get: () => boolean
        set: (value: boolean) => void
    }
    _dispatch_event: (event: unknown) => void
}

export type ModuleProxy = BaseModule & Record<string, unknown>

const RESERVED_NAMES = [
    'state',
    'ui',
    'events',
    'log',
    'block',
    'editable',
    '_dispatch_event',
] as const

export class PythonModule {
    private extensions: Map<string, ModuleExtension> = new Map()
    private isLocked = false

    constructor(
        private stateStore: StateStore,
        private eventRegistry: EventRegistry,
        private logger: Logger,
        private blockEngine: BlockEngine
    ) {}

    registerExtension(name: string, extension: ModuleExtension): void {
        this.ensureNotLocked()

        if (this.extensions.has(name)) {
            console.warn(`Extension '${name}' is already registered and will be overwritten`, {
                extensionName: name,
            })
        }

        this.validateExtensionName(name)

        this.extensions.set(name, extension)
    }

    wrapExtensions() {
        const executor = PythonExecutor.getInstance()

        this.extensions.forEach((extension, name) => {
            const wrappedExtension: ModuleExtension = {}
            Object.entries(extension).forEach(([key, value]) => {
                if (typeof value === 'function') {
                    wrappedExtension[key] = this.wrapFunctionWithConversion(
                        value as (...args: unknown[]) => unknown
                    )
                } else {
                    wrappedExtension[key] = executor.toPython(value)
                }
            })
            this.extensions.set(name, wrappedExtension)
        })
    }

    unregisterExtension(name: string): boolean {
        this.ensureNotLocked()
        return this.extensions.delete(name)
    }

    hasExtension(name: string): boolean {
        return this.extensions.has(name)
    }

    getExtension(name: string): ModuleExtension | undefined {
        return this.extensions.get(name)
    }

    getExtensionNames(): string[] {
        return Array.from(this.extensions.keys())
    }

    createModuleProxy(): ModuleProxy {
        const baseModuleProxy = this.createBaseModuleProxy()
        const extendedProxy = { ...baseModuleProxy } as ModuleProxy

        this.extensions.forEach((extension, name) => {
            extendedProxy[name] = extension
        })

        this.isLocked = true
        return extendedProxy
    }

    clear(): void {
        this.extensions.clear()
        this.isLocked = false
    }

    isModuleLocked(): boolean {
        return this.isLocked
    }

    private wrapFunctionWithConversion<T extends (...args: unknown[]) => unknown>(
        fn: T
    ): (...args: unknown[]) => unknown {
        const executor = PythonExecutor.getInstance()
        const logger = this.logger
        const wrappedFn = function (...args: unknown[]) {
            try {
                const convertedArgs = args.map(arg => {
                    try {
                        return executor.toJS(arg)
                    } catch (conversionError) {
                        logger.warn(
                            'Failed to convert argument from Python to JS, using raw value',
                            {
                                source: 'PythonModule',
                                error:
                                    conversionError instanceof Error
                                        ? conversionError
                                        : String(conversionError),
                            }
                        )
                        return arg
                    }
                })
                const result = fn(...(convertedArgs as Parameters<T>))
                try {
                    return executor.toPython(result)
                } catch (conversionError) {
                    logger.warn('Failed to convert result from JS to Python, returning raw value', {
                        source: 'PythonModule',
                        error:
                            conversionError instanceof Error
                                ? conversionError
                                : String(conversionError),
                    })
                    return result
                }
            } catch (error) {
                logger.error('Error in wrapped function call', {
                    source: 'PythonModule',
                    error: error instanceof Error ? error : String(error),
                })
                return undefined
            }
        }
        return wrappedFn
    }

    private createBaseModuleProxy(): BaseModule {
        const store = this.stateStore
        const registry = this.eventRegistry
        const logger = this.logger
        const blockEngine = this.blockEngine
        const executor = PythonExecutor.getInstance()

        return {
            state: {
                get: (key: string, defaultValue?: unknown) => {
                    try {
                        const val = store.get(key) ?? defaultValue
                        return executor.toPython(val)
                    } catch (error) {
                        logger.error(`Failed to get state "${key}"`, {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: { key },
                        })
                        return defaultValue !== undefined
                            ? executor.toPython(defaultValue)
                            : undefined
                    }
                },

                set: (key: string, value: unknown) => {
                    try {
                        const jsVal = executor.toJS(value)
                        store.set(key, jsVal)
                    } catch (error) {
                        logger.error(`Failed to set state "${key}"`, {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: {
                                key,
                                hint: 'The value may be of an unsupported type. Ensure you are passing JSON-compatible values (strings, numbers, booleans, lists, dicts).',
                            },
                        })
                    }
                },
            },

            events: {
                register: this.wrapFunctionWithConversion(((
                    selectorType: string,
                    selectorValue: string,
                    eventType: string,
                    handlerId: string
                ) => {
                    try {
                        console.log('[FTS] events.register called:', {
                            selectorType,
                            selectorValue,
                            eventType,
                            handlerId,
                        })
                        registry.register(selectorType, selectorValue, eventType, handlerId)
                        return handlerId
                    } catch (error) {
                        logger.error('Failed to register event handler', {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: { selectorType, selectorValue, eventType, handlerId },
                        })
                        return handlerId
                    }
                }) as (...args: unknown[]) => unknown) as BaseModule['events']['register'],

                unregister: this.wrapFunctionWithConversion(((
                    selectorType: string,
                    selectorValue: string,
                    eventType: string,
                    handlerId?: string
                ) => {
                    try {
                        registry.unregister(selectorType, selectorValue, eventType, handlerId)
                    } catch (error) {
                        logger.error('Failed to unregister event handler', {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: { selectorType, selectorValue, eventType, handlerId },
                        })
                    }
                }) as (...args: unknown[]) => unknown) as BaseModule['events']['unregister'],
            },

            ui: {
                showMessage: this.wrapFunctionWithConversion(((msg: string, type = 'info') => {
                    try {
                        const fn =
                            (message as unknown as Record<string, (m: string) => void>)[
                                type as string
                            ] || message.info
                        fn(String(msg))
                    } catch (error) {
                        logger.error('Failed to show message', {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: { msg: String(msg), type },
                        })
                    }
                }) as (...args: unknown[]) => unknown) as BaseModule['ui']['showMessage'],

                showModal: this.wrapFunctionWithConversion(((title: string, content: string) => {
                    try {
                        Modal.info({
                            title: String(title),
                            content: String(content),
                            centered: true,
                        })
                    } catch (error) {
                        logger.error('Failed to show modal', {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                            context: { title: String(title) },
                        })
                    }
                }) as (...args: unknown[]) => unknown) as BaseModule['ui']['showModal'],
            },

            log: {
                error: this.wrapFunctionWithConversion(((
                    msg: string,
                    error: string,
                    context: Record<string, unknown>,
                    source: string
                ) => {
                    const safeMsg = typeof msg === 'string' ? msg : JSON.stringify(msg)
                    logger.error(safeMsg, { source: source, error: error, context: context })
                }) as (...args: unknown[]) => unknown) as BaseModule['log']['error'],
                warn: this.wrapFunctionWithConversion(((
                    msg: string,
                    context: Record<string, unknown>,
                    source: string
                ) => {
                    const safeMsg = typeof msg === 'string' ? msg : JSON.stringify(msg)
                    logger.warn(safeMsg, { source: source, context: context })
                }) as (...args: unknown[]) => unknown) as BaseModule['log']['warn'],
                info: this.wrapFunctionWithConversion(((
                    msg: string,
                    context: Record<string, unknown>,
                    source: string
                ) => {
                    const safeMsg = typeof msg === 'string' ? msg : JSON.stringify(msg)
                    logger.info(safeMsg, { source: source, context: context })
                }) as (...args: unknown[]) => unknown) as BaseModule['log']['info'],
                debug: this.wrapFunctionWithConversion(((
                    msg: string,
                    context: Record<string, unknown>,
                    source: string
                ) => {
                    const safeMsg = typeof msg === 'string' ? msg : JSON.stringify(msg)
                    logger.debug(safeMsg, { source: source, context: context })
                }) as (...args: unknown[]) => unknown) as BaseModule['log']['debug'],
            },

            block: {
                getById: (blockId: string) => {
                    try {
                        const block = blockEngine.getBlock(blockId)
                        if (!block) {
                            return undefined
                        }
                        return createBlockProxy(block, this.wrapFunctionWithConversion.bind(this))
                    } catch (error) {
                        logger.error(`Failed to get block by id "${blockId}"`, {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                        })
                        return undefined
                    }
                },
                getByName: (name: string) => {
                    try {
                        const blocks = blockEngine.getBlockByName(name)
                        if (!blocks?.length) {
                            return undefined
                        }
                        return blocks.map(block =>
                            createBlockProxy(block, this.wrapFunctionWithConversion.bind(this))
                        )
                    } catch (error) {
                        logger.error(`Failed to get block by name "${name}"`, {
                            source: 'PythonModule',
                            error: error instanceof Error ? error : String(error),
                        })
                        return undefined
                    }
                },
            },

            editable: {
                get: this.wrapFunctionWithConversion((() => {
                    return blockEngine.isEditable()
                }) as (...args: unknown[]) => unknown) as BaseModule['editable']['get'],

                set: this.wrapFunctionWithConversion(((value: boolean) => {
                    blockEngine.setEditable(value)
                }) as (...args: unknown[]) => unknown) as BaseModule['editable']['set'],
            },

            _dispatch_event: (() => {
                // This is handled by Python wrapper, but we need it in the proxy
                // The actual dispatch happens in executeHandlers
                // The parameter is required for the proxy interface but not used here
            }) as (event: unknown) => void,
        }
    }

    private validateExtensionName(name: string): void {
        if (RESERVED_NAMES.includes(name as (typeof RESERVED_NAMES)[number])) {
            throw new Error(
                `Extension name '${name}' is reserved. Reserved names: ${RESERVED_NAMES.join(', ')}`
            )
        }
    }

    private ensureNotLocked(): void {
        if (this.isLocked) {
            throw new Error(
                'Cannot modify extensions after module proxy has been created. ' +
                    'Extensions must be registered before calling createModuleProxy().'
            )
        }
    }
}
